import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PropertyPurchasePayment {
  id: string;
  fixed_asset_id: string;
  user_id: string;
  amount: number;
  payment_date: string;
  description: string | null;
  receipt_number: string | null;
  transaction_id: string | null;
  created_at: string;
}

export function usePropertyPurchasePayments(fixedAssetId: string | undefined) {
  return useQuery({
    queryKey: ['property-purchase-payments', fixedAssetId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('property_purchase_payments')
        .select('*')
        .eq('fixed_asset_id', fixedAssetId!)
        .order('payment_date', { ascending: false });
      if (error) throw error;
      return data as PropertyPurchasePayment[];
    },
    enabled: !!fixedAssetId,
  });
}

export function useRecordPurchasePayment() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (payment: Omit<PropertyPurchasePayment, 'id' | 'created_at' | 'user_id'>) => {
      // Insert the payment record
      const { data, error } = await supabase
        .from('property_purchase_payments')
        .insert({ ...payment, user_id: user!.id })
        .select()
        .single();
      if (error) throw error;

      // Update amount_paid on the fixed asset
      // Fetch current amount_paid, add new payment
      const { data: asset, error: assetError } = await supabase
        .from('fixed_assets')
        .select('amount_paid')
        .eq('id', payment.fixed_asset_id)
        .single();
      if (assetError) throw assetError;

      const newAmountPaid = (Number(asset.amount_paid) || 0) + Number(payment.amount);
      const { error: updateError } = await supabase
        .from('fixed_assets')
        .update({ amount_paid: newAmountPaid })
        .eq('id', payment.fixed_asset_id);
      if (updateError) throw updateError;

      return data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['property-purchase-payments', variables.fixed_asset_id] });
      qc.invalidateQueries({ queryKey: ['fixed-assets'] });
      qc.invalidateQueries({ queryKey: ['fixed-asset-detail', variables.fixed_asset_id] });
    },
  });
}
